import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { formatNumberWithSubscript, formatCurrencyWithSubscript } from "../../utils/helpers";

const CompactNumberWrapper = styled.span`
  display: inline-flex;
  align-items: baseline;
  font-variant-numeric: tabular-nums;
`;

const SubscriptNumber = styled.span`
  font-size: 1.5rem;
  vertical-align: sub;
  line-height: 0;
  margin: 0 1px;
`;

const FullValueTooltip = styled.div`
  display: none;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #111522;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  
  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #111522;
  }
`;

const CompactNumberContainer = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: ${props => props.showTooltip ? "help" : "default"};
  
  &:hover ${FullValueTooltip} {
    display: block !important;
  }
`;

const FullValueDisplay = styled.div`
  display: inline-flex;
  padding: 6px 12px;
  background: rgba(31, 41, 55, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  font-size: 12px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const FormattedNumber = ({ 
  value, 
  symbol = "", 
  maxDecimals = 8, 
  showTooltip = true,
  variant = "compact",
  className,
  style 
}) => {
  const formatResult = symbol 
    ? formatCurrencyWithSubscript(value, symbol, maxDecimals)
    : formatNumberWithSubscript(value, maxDecimals);

  if (variant === "full") {
    const fullValue = formatResult.useSubscript && formatResult.parts?.fullValue
      ? (formatResult.parts.symbol ? `${formatResult.parts.symbol}${formatResult.parts.fullValue}` : formatResult.parts.fullValue)
      : formatResult.formatted;
    
    return (
      <FullValueDisplay className={className} style={style}>
        {fullValue}
      </FullValueDisplay>
    );
  }

  if (!formatResult.useSubscript) {
    return (
      <span className={className} style={style}>
        {formatResult.formatted}
      </span>
    );
  }

  const { parts } = formatResult;

  return (
    <CompactNumberContainer 
      className={className}
      style={style}
      showTooltip={showTooltip && parts?.fullValue}
    >
      <CompactNumberWrapper>
        {parts.symbol && <span>{parts.symbol}</span>}
        <span>{parts.prefix}</span>
        <SubscriptNumber>{parts.subscript}</SubscriptNumber>
        <span>{parts.significant}</span>
      </CompactNumberWrapper>
      {showTooltip && parts?.fullValue && (
        <FullValueTooltip>
          {parts.symbol ? `${parts.symbol}${parts.fullValue}` : parts.fullValue}
        </FullValueTooltip>
      )}
    </CompactNumberContainer>
  );
};

FormattedNumber.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  symbol: PropTypes.string,
  maxDecimals: PropTypes.number,
  showTooltip: PropTypes.bool,
  variant: PropTypes.oneOf(["compact", "full"]),
  className: PropTypes.string,
  style: PropTypes.object,
};

export default FormattedNumber;

